/**
 * Live voice controls: a "Talk live" button while idle, then the connection state, live captions
 * of both speakers, mute and End. Captions are display-only; the stored transcript appears in the
 * conversation after the session ends.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentLivePanel
 */

import { AudioLines, Loader2, Mic, MicOff, PhoneOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { LivePhase } from '@/lib/fluxAgentLive';
import { cn } from '@/lib/utils';
import type { FluxAgentLiveVoice } from './useFluxAgentLiveVoice';

/** Caption for each phase. */
export const LIVE_LABELS: Record<LivePhase, string> = {
  idle: '',
  connecting: 'Connecting live voice…',
  live: 'Live — just talk, interrupt any time',
  reconnecting: 'Reconnecting…',
  closing: 'Finishing up…',
  closed: 'Live conversation ended',
  failed: 'Live voice could not start',
};

/** Inputs for {@link FluxAgentLivePanel}. */
export interface FluxAgentLivePanelProps {
  /** State and actions from `useFluxAgentLiveVoice`. */
  live: FluxAgentLiveVoice;
  /** Most recent caption bubbles to show (default 4). */
  maxCaptions?: number;
}

/** Render live voice controls and captions. */
export function FluxAgentLivePanel({ live, maxCaptions = 4 }: FluxAgentLivePanelProps) {
  const { phase, active } = live;
  if (!active) {
    return (
      <div className="flex flex-wrap items-center gap-2" data-live-state={phase}>
        <Button type="button" variant="outline" size="sm" onClick={() => void live.start()}>
          <AudioLines className="mr-1.5 h-4 w-4" /> Talk live
        </Button>
        {live.error && (
          <span role="alert" className="text-destructive text-sm">
            {live.error}
          </span>
        )}
      </div>
    );
  }
  const captions = live.captions.slice(-maxCaptions);
  return (
    <section
      aria-label="Live voice"
      className="border-primary/30 bg-primary/5 space-y-2 rounded-xl border px-3 py-2.5"
      data-live-state={phase}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-medium" aria-live="polite">
          {phase === 'live' ? (
            <AudioLines className="text-primary h-4 w-4" />
          ) : (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          {LIVE_LABELS[phase]}
        </p>
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-pressed={live.muted}
            onClick={() => live.setMuted(!live.muted)}
            disabled={phase !== 'live'}
          >
            {live.muted ? <MicOff className="mr-1 h-4 w-4" /> : <Mic className="mr-1 h-4 w-4" />}
            {live.muted ? 'Unmute' : 'Mute'}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => void live.stop()}
            disabled={phase === 'closing'}
          >
            <PhoneOff className="mr-1 h-4 w-4" /> End
          </Button>
        </div>
      </div>
      {captions.length > 0 && (
        <ol className="space-y-1 text-sm" aria-label="Live captions">
          {captions.map((caption, index) => (
            <li
              key={`${live.captions.length - captions.length + index}`}
              data-speaker={caption.speaker}
              className={cn(
                'rounded-lg px-2.5 py-1.5',
                caption.speaker === 'user'
                  ? 'bg-primary text-primary-foreground ml-8'
                  : 'bg-muted mr-8'
              )}
            >
              {caption.text}
            </li>
          ))}
        </ol>
      )}
      {live.error && (
        <p role="alert" className="text-destructive text-sm">
          {live.error}
        </p>
      )}
    </section>
  );
}
